/**
 * Bed PDP — mattress upsell (snippets/product-mattress-upsell.liquid).
 * - Keeps every mattress card on the variant matching the bed's selected size.
 * - When a mattress is ticked, adds it to cart right before the bed, through the
 *   theme's own product-form submission (so the cart drawer opens with both).
 * - While a mattress is ticked, the ATC button reads "add the set" and a
 *   "or add the bed only · price" link appears under it.
 */
if (!customElements.get('mattress-upsell')) {
  class MattressUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.discountPercent = Number(this.dataset.discountPercent) || 0;
      this.badgeText = this.dataset.badgeText || '[amount]';
      this.bedSizes = this.readJson(this.querySelector('[data-bed-sizes]')) || {};
      this.bedPrices = this.readJson(this.querySelector('[data-bed-prices]')) || {};
      this.items = Array.from(this.querySelectorAll('[data-mattress-item]')).map((el) => ({
        el,
        checkbox: el.querySelector('[data-mattress-checkbox]'),
        variants: this.readJson(el.querySelector('[data-mattress-variants]')) || [],
        title: el.querySelector('.mattress-upsell__title')?.textContent.trim() || '',
        finalEl: el.querySelector('[data-price-final]'),
        compareEl: el.querySelector('[data-price-compare]'),
        badgeEl: el.querySelector('[data-price-badge]'),
        moreEl: el.querySelector('[data-mattress-url]'),
      }));

      // One mattress per bed: ticking one clears the others (still possible to untick all).
      this.items.forEach(({ checkbox }) => {
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            this.items.forEach((item) => {
              if (item.checkbox !== checkbox) item.checkbox.checked = false;
            });
          }
          this.emitSelection();
        });
      });

      this.onVariantChange = () => this.syncSize();
      document.addEventListener('variant:changed', this.onVariantChange);
      // The browser may restore a ticked checkbox after init (reload / back-forward).
      this.onPageShow = () => this.emitSelection();
      window.addEventListener('pageshow', this.onPageShow);
      this.form?.addEventListener('change', this.onVariantChange);

      this.hookForm();
      this.renderBedOnly();
      this.syncSize();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChange);
      window.removeEventListener('pageshow', this.onPageShow);
      this.form?.removeEventListener('change', this.onVariantChange);
    }

    get form() {
      return document.getElementById(this.dataset.formId);
    }

    readJson(el) {
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    formatMoney(cents) {
      const format = window.FoxTheme?.settings?.moneyFormat;
      if (window.FoxTheme?.Currency?.formatMoney && format) {
        return FoxTheme.Currency.formatMoney(cents, format);
      }
      return `${Math.round(cents / 100).toLocaleString('en-US')} ₪`;
    }

    currentBedSize() {
      const input = this.form?.querySelector('input[name="id"]');
      return input ? this.bedSizes[String(input.value)] || '' : '';
    }

    syncSize() {
      const size = this.currentBedSize();
      if (!size) return;

      this.querySelectorAll('[data-mattress-size]').forEach((el) => {
        el.textContent = size;
      });

      this.items.forEach((item) => {
        const variant = item.variants.find((v) => v.size === size && v.available);
        item.el.classList.toggle('hidden', !variant);
        item.checkbox.disabled = !variant;
        if (!variant) {
          item.checkbox.checked = false;
          return;
        }

        item.checkbox.value = variant.id;
        const finalPrice = Math.round((variant.price * (100 - this.discountPercent)) / 10000) * 100;
        const compare = Math.max(variant.compare || 0, variant.price);
        const saving = compare - finalPrice;

        item.finalEl.textContent = this.formatMoney(finalPrice);
        item.compareEl.textContent = this.formatMoney(compare);
        item.compareEl.classList.toggle('hidden', saving <= 0);
        item.badgeEl.textContent = this.badgeText.replace('[amount]', this.formatMoney(saving));
        item.badgeEl.classList.toggle('hidden', saving <= 0);
        if (item.moreEl) {
          item.moreEl.href = `${item.moreEl.dataset.mattressUrl}?variant=${variant.id}`;
        }
        item.finalPrice = finalPrice;
        item.compare = compare;
        item.size = size;
      });
      this.emitSelection();
    }

    /** Current pick (or null), read by the bundle summary block. */
    get selection() {
      const item = this.items?.find(({ checkbox }) => checkbox.checked && !checkbox.disabled);
      if (!item) return null;
      return { id: Number(item.checkbox.value), title: item.title, size: item.size, price: item.finalPrice, compare: item.compare };
    }

    emitSelection() {
      document.dispatchEvent(new CustomEvent('mattress:change', { detail: { selection: this.selection } }));
      // Deferred so the theme's own ATC label sync (variant change) runs first.
      setTimeout(() => this.updateBuyButtons(), 0);
    }

    /** "או הוספת המיטה בלבד · price" — placed right under the bed form's buttons. */
    renderBedOnly() {
      const buttons = this.form?.querySelector('.product-form__buttons');
      if (!buttons || !this.dataset.bedOnlyLabel) return;

      this.bedOnlyEl = document.createElement('p');
      this.bedOnlyEl.className = 'mattress-bed-only hidden';
      const prefix = this.dataset.bedOnlyPrefix ? `${this.dataset.bedOnlyPrefix} ` : '';
      this.bedOnlyEl.append(prefix);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mattress-bed-only__button';
      button.textContent = this.dataset.bedOnlyLabel;
      button.addEventListener('click', () => {
        const submit = this.form.querySelector('.product-form__submit[name="add"]');
        if (!submit || submit.disabled) return;
        this.skipMattress = true;
        this.form.requestSubmit(submit);
        this.skipMattress = false;
      });
      this.bedOnlyEl.append(button);

      this.bedOnlyPriceEl = document.createElement('span');
      this.bedOnlyPriceEl.className = 'mattress-bed-only__price';
      this.bedOnlyEl.append(' · ', this.bedOnlyPriceEl);

      buttons.after(this.bedOnlyEl);
    }

    /** Main + sticky ATC labels say "add the set" while a mattress is ticked. */
    updateBuyButtons() {
      const form = this.form;
      if (!form) return;
      const selection = this.selection;
      const variantId = form.querySelector('input[name="id"]')?.value;
      const sectionId = form.closest('[data-section-id]')?.dataset.sectionId;
      const bundleLabel = this.dataset.bundleAtcLabel;

      const labels = [
        ...form.querySelectorAll('.product-form__submit[name="add"] .js-kuala-atc-label'),
        ...document.querySelectorAll(`[form="${this.dataset.formId}"] [data-sticky-cta-label]`),
      ];
      labels.forEach((el) => {
        const button = el.closest('button');
        if (selection && bundleLabel && !button?.disabled) {
          if (!el.hasAttribute('data-mattress-label')) el.dataset.defaultLabel = el.textContent.trim();
          el.textContent = bundleLabel;
          el.setAttribute('data-mattress-label', '');
        } else if (el.hasAttribute('data-mattress-label')) {
          el.textContent =
            typeof window.kualaPickAtcLabel === 'function' && variantId && sectionId
              ? window.kualaPickAtcLabel(variantId, sectionId)
              : el.dataset.defaultLabel || el.textContent;
          el.removeAttribute('data-mattress-label');
        }
        if (button) button.setAttribute('aria-label', el.textContent.trim());
      });

      if (this.bedOnlyEl) {
        const bedPrice = this.bedPrices[String(variantId)];
        this.bedOnlyEl.classList.toggle('hidden', !selection || bedPrice == null);
        if (bedPrice != null) this.bedOnlyPriceEl.textContent = this.formatMoney(bedPrice);
      }
    }

    selectedMattressId() {
      const checked = this.items.find(({ checkbox }) => checkbox.checked && !checkbox.disabled);
      return checked ? Number(checked.checkbox.value) : null;
    }

    /**
     * Wraps the theme ProductForm's `handleFormSubmission` (an own instance property)
     * so the mattress is added first; the bed request then returns the re-rendered cart.
     */
    hookForm() {
      const form = this.form;
      if (!form) return;

      const wrap = () => {
        if (form.mattressUpsellHooked || typeof form.handleFormSubmission !== 'function') return;
        form.mattressUpsellHooked = true;
        const original = form.handleFormSubmission;

        form.handleFormSubmission = async (config) => {
          const mattressId = this.skipMattress ? null : this.selectedMattressId();
          if (!mattressId) return original(config);

          const quantity = parseInt(config.body?.get?.('quantity'), 10) || 1;
          try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [
                  {
                    id: mattressId,
                    quantity,
                    properties: { _bed_bundle: this.dataset.bedHandle },
                  },
                ],
              }),
            });
            const result = await response.json();
            if (!response.ok || result.status) {
              throw new Error(result.description || result.message || 'Mattress add failed');
            }
          } catch (error) {
            console.error(error);
            form.displayFormErrors?.(error.message);
            form.submitButtonElement?.classList.remove('btn--loading');
            form.submitButtonElement?.removeAttribute('aria-disabled');
            return;
          }
          return original(config);
        };
      };

      wrap();
      // In case the theme's product-form upgrades after this script runs.
      document.addEventListener('submit', (event) => event.target === form && wrap(), true);
    }
  }

  customElements.define('mattress-upsell', MattressUpsell);
}
